import { Badge } from '@react-apps/design-system';
import type { BadgeProps } from '@react-apps/design-system';
import type { TotvsSyncAttempt } from '../../domain/entities/totvs.entity';
import { formatDateTime } from '../display';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

function resultVariant(result: string): BadgeVariant {
  const normalized = result.toLowerCase();
  if (normalized.includes('deliver') || normalized.includes('success')) {
    return 'success';
  }
  if (normalized.includes('fail') || normalized.includes('error')) {
    return 'error';
  }
  if (normalized.includes('wait') || normalized.includes('skip')) {
    return 'warning';
  }
  return 'default';
}

function CodeBlock({ label, value }: { label: string; value: string | null }) {
  return (
    <details className="group">
      <summary className="text-(--color-text-secondary) hover:text-(--color-accent) flex cursor-pointer list-none items-center gap-1 text-xs font-medium transition-colors">
        <svg
          className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m8.25 4.5 7.5 7.5-7.5 7.5"
          />
        </svg>
        {label}
      </summary>
      <pre className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-secondary) mt-2 max-h-72 overflow-auto rounded-lg border p-3 text-xs leading-relaxed">
        {value ?? '—'}
      </pre>
    </details>
  );
}

function AttemptRow({
  attempt,
  isLast,
}: {
  attempt: TotvsSyncAttempt;
  isLast: boolean;
}) {
  return (
    <li className="relative flex gap-4">
      {!isLast && (
        <span
          aria-hidden="true"
          className="bg-(--color-border) left-3.75 absolute -bottom-6 top-8 w-px"
        />
      )}
      <div className="border-(--color-border) bg-(--color-bg-primary) relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border">
        <span
          className={`h-2 w-2 rounded-full ${
            resultVariant(attempt.result) === 'success'
              ? 'bg-emerald-500'
              : resultVariant(attempt.result) === 'error'
                ? 'bg-red-500'
                : 'bg-(--color-text-secondary)'
          }`}
        />
      </div>

      <div className="min-w-0 flex-1 pb-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={resultVariant(attempt.result)}>
              {attempt.result}
            </Badge>
            {attempt.errorCode && (
              <span className="text-(--color-text-secondary) font-mono text-xs">
                {attempt.errorCode}
              </span>
            )}
          </div>
          <span className="text-(--color-text-secondary) text-xs">
            {formatDateTime(attempt.attemptedAt)}
          </span>
        </div>

        {attempt.errorMessage && (
          <p className="text-(--color-text-primary) mt-1.5 text-sm">
            {attempt.errorMessage}
          </p>
        )}

        {(attempt.request || attempt.rawResponse) && (
          <div className="mt-2 space-y-2">
            {attempt.request && (
              <CodeBlock label="Ver request enviado" value={attempt.request} />
            )}
            {attempt.rawResponse && (
              <CodeBlock
                label="Ver resposta crua"
                value={attempt.rawResponse}
              />
            )}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Timeline de tentativas no RM (§9.3), no mesmo padrão visual do histórico de
 * eventos de `sales` (`transaction-event-row`): bolinha de status por tentativa,
 * data, erro classificado e request/resposta crua atrás de painéis expansíveis.
 * As tentativas chegam da mais antiga para a mais recente e são exibidas invertidas.
 */
export function TotvsAttemptsTimeline({
  attempts,
}: {
  attempts: TotvsSyncAttempt[];
}) {
  if (attempts.length === 0) {
    return (
      <p className="text-(--color-text-secondary) py-8 text-center text-sm">
        Nenhuma tentativa registrada.
      </p>
    );
  }

  const ordered = [...attempts].reverse();

  return (
    <ol>
      {ordered.map((attempt, i) => (
        <AttemptRow
          key={`${attempt.attemptedAt}-${attempt.result}-${i}`}
          attempt={attempt}
          isLast={i === ordered.length - 1}
        />
      ))}
    </ol>
  );
}
